import { useState } from "react";
import { authHelpers } from "../../lib/supabase";
import logger from "../../shared/utils/logger";
import { setSignupAttestationFlag } from "../../shared/utils/signupAttestation";

/**
 * Google OAuth sign-in button component
 * Can be used on both signup and signin pages.
 *
 * `attestOver18` MUST be set to true ONLY on the SIGN UP surface, where the
 * affirmation microcopy shown at the point of this action ("By continuing, you
 * confirm you are 18 or older...") is the user's informed 18+ consent. When set,
 * we persist a one-shot flag before the OAuth redirect so AuthContext can stamp
 * age_verified inline on return and the new user never sees the DobGate popup.
 * It is deliberately left OFF on the sign-in surface: an existing user signing
 * in must never be treated as a fresh attestation.
 */
const GoogleAuthButton = ({
  className = "",
  disabled = false,
  label = "Continue with Google",
  attestOver18 = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleGoogleSignIn = async () => {
    if (isLoading || disabled) return;

    setIsLoading(true);
    setError(null);

    // No pre-flight geo check here. Sign-up is already page-level gated
    // in SignupPage.jsx via uiGeoBlockingService.shouldShowSignupUI(),
    // so AU/blocked users never see this button on the signup flow.
    // For sign-in, blocking would lock out admins traveling outside US
    // (admins are exempt from the requirePlayer geo gate but the JWT
    // they need to obtain shouldn't be blocked at the OAuth handoff).
    // Backend geoBlockRegistration on /trigger-registration-event still
    // refuses to stamp geo_verified=true for blocked-region IPs, so a
    // non-admin who somehow reaches /signin from AU and signs in via
    // Google ends up with a valid auth session but geo_verified=false,
    // which requirePlayer still rejects on every /api/casino/* call.

    // Signup surface only: record the 18+ attestation BEFORE the redirect so it
    // survives the round-trip to Google and is read on OAuth return. The
    // affirmation microcopy beside this button is the point-of-action consent.
    if (attestOver18) {
      setSignupAttestationFlag();
    }

    try {
      const { error } = await authHelpers.signInWithGoogle();

      if (error) {
        logger.error("[GoogleAuthButton] OAuth error:", error);
        setError(error.message || "Failed to sign in with Google");
        setIsLoading(false);
      }
      // If no error, Supabase will redirect to Google
      // Loading state stays true during redirect
    } catch (err) {
      logger.error("[GoogleAuthButton] Unexpected error:", err);
      setError("An unexpected error occurred");
      setIsLoading(false);
    }
  };

  return (
    <div className={className}>
      <button
        type="button"
        onClick={handleGoogleSignIn}
        disabled={isLoading || disabled}
        className={`
          w-full flex items-center justify-center gap-3 
          px-4 py-3 
          bg-white hover:bg-gray-100 
          border border-gray-300 
          rounded-lg 
          text-gray-700 font-medium
          transition-colors duration-200
          disabled:opacity-50 disabled:cursor-not-allowed
          ${isLoading ? "cursor-wait" : "cursor-pointer"}
        `}
      >
        {/* Google "G" Logo SVG */}
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>

        <span>{isLoading ? "Redirecting..." : label}</span>
      </button>

      {error && (
        <p className="text-red-500 text-sm mt-2 text-center">{error}</p>
      )}
    </div>
  );
};

export default GoogleAuthButton;
